import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Stack, Button, Container } from '@mui/material';

import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useFormContext } from '../../hooks/useForm';
import SectionPanel from './formViewer/SectionPanel';

FormPreview.propTypes = {
  disabled: PropTypes.bool,
  addTicket: PropTypes.func,
  handleBackdropOpen: PropTypes.func,
  handleBackdropClose: PropTypes.func,
  formSharableSettings: PropTypes.object,
};

export default function FormPreview({
  disabled,
  addTicket,
  handleBackdropOpen,
  handleBackdropClose,
  formSharableSettings,
}) {
  const navigate = useNavigate();
  const { id, isAuthenticated } = useAuth();
  const {
    getOrderedSections,
    updateSectionValidity,
    getSectionQuestions,
    getFormData,
    formatAttachements,
  } = useFormContext();

  const [sectionsList, setSections] = useState([]);
  const [page, setPage] = useState(0);
  const { showToast } = useToast();
  const [formData, setFormData] = useState([]);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [hasSelectedSection, setHasSelectedSection] = useState(false);
  const [searchParams] = useSearchParams();
  const formToken = searchParams.get('form');

  const { t } = useTranslation();
  const [startTime] = useState(new Date());

  useEffect(() => {
    setSections(getOrderedSections() || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitTicket = () => {
    const currentSection = sectionsList[page];
    if (!updateSectionValidity(currentSection.id)) {
      showToast({ message: t('formControl.makeSureAllFields'), severity: 'error' });
      return;
    }

    if (isDebouncing) return;
    setIsDebouncing(true);
    handleBackdropOpen();

    const finalData = [
      ...formData,
      {
        sectionId: currentSection.id,
        sectionName: currentSection.name,
        questions: getSectionQuestions(currentSection.id),
      },
    ];

    const attachements = formatAttachements();
    const contextData = getFormData();
    const { priority, duration, category, agents, autoAssign, tags } = currentSection;

    addTicket({
      userId: id,
      data: finalData,
      priority,
      duration,
      category,
      attachments: attachements,
      description: contextData.formData.ticketDescription,
      agents,
      autoAssign,
      tags,
      startTime,
      endTime: new Date().toISOString(),
      isHidden: formSharableSettings?.isHidden || false,
    })
      .then(() => {
        handleBackdropClose();
        if (isAuthenticated) {
          navigate(
            formSharableSettings?.isHidden
              ? '/dashboard/shareableForms'
              : '/dashboard/tickets'
          );
          showToast({
            message: t('successMessages.successTicketAdd'),
            severity: 'success',
          });
        } else {
          navigate('/form-sharable-submitted', {
            state: {
              isMultipleSubmission: formSharableSettings.isMultipleSubmission,
              isAnonymous: formSharableSettings.isAnonymous,
            },
          });
        }
      })
      .catch((error) => {
        handleBackdropClose();
        showToast({
          message:
            error.response?.status === 409
              ? t('errorMessages.alreadySubmittedTheForm')
              : t('errorMessages.errorCreatingTicket'),
          severity: 'error',
        });
      })
      .finally(() => setTimeout(() => setIsDebouncing(false), 300));
  };

  return (
    <Box sx={{ pb: 5 }}>
      {sectionsList?.slice(page, page + 1).map((section) => (
        <SectionPanel
          key={section.id}
          section={section}
          sectionsList={sectionsList}
          page={page}
          setSections={setSections}
          setPage={setPage}
          setHasSelectedSection={setHasSelectedSection}
          hasSelectedSection={hasSelectedSection}
        />
      ))}

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 5 }}
          spacing={2}
        >
          {page > 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                setFormData(formData.slice(0, -1));
                setPage(page - 1);
              }}
            >
              {t('buttons.back')}
            </Button>
          )}

          {page < sectionsList.length - 1 &&
            (hasSelectedSection || !sectionsList[page]?.nextSection) && (
              <Button
                variant="contained"
                onClick={() => {
                  setFormData([
                    ...formData,
                    {
                      sectionId: sectionsList[page].id,
                      sectionName: sectionsList[page].name,
                      questions: getSectionQuestions(sectionsList[page].id),
                    },
                  ]);
                  setPage(page + 1);
                }}
              >
                {t('buttons.next')}
              </Button>
            )}

          {(page === sectionsList.length - 1 ||
            (sectionsList[page]?.nextSection === 'submit' && !hasSelectedSection)) && (
            <Button
              variant="contained"
              sx={{
                background:
                  !formToken && isAuthenticated
                    ? 'primary.main'
                    : 'linear-gradient(135deg, #56b499, #e2dca5)',
                minWidth: '150px',
              }}
              onClick={submitTicket}
              disabled={disabled || isDebouncing}
            >
              {t('buttons.submit')}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
